import React, {
    useEffect,
    useState,
    useCallback,
    Fragment,
    useRef,
    useContext,
} from 'react';
import { PricingCard, PricingCardSkeleton } from './components';
import { useApi } from '../../Helpers/ApiHook';
import { AuthContext } from '../../Contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';

// const plan_data = [
//     {
//         id: 1,
//         category: "membership",
//         name: "Basic Plan",
//         price: "4999.00",
//         duration_months: 12,
//         description: "Unlock advanced benefits for ultimate results.",
//         features: [
//             { name: "Official Affiliations Certificate (Valid for 2 Years)", status: true },
//             { name: "Professional Verifiable ID Card", status: true },
//             { name: "Appointment Letter", status: true },
//             { name: "Professional Website (24 Months)", status: true },
//             { name: "National-Level Recognition & Credibility", status: true },
//             { name: "Social Media Visibility", status: true },
//             { name: "Access Online Webinars", status: true },
//             { name: "Discounts on Partner Supplements", status: true },
//             { name: "Discounts on Fitness & Nutrition Courses", status: true },
//             { name: "Priority in Job Placements", status: true },
//             { name: "Eligibility for Approved Training Provider Status", status: true },
//         ],
//         created_at: "2025-11-21T00:59:10.000000Z",
//         updated_at: "2025-11-21T00:59:10.000000Z"
//     },
// ]

const MembershipPlans = () => {

    const navigate = useNavigate();


    const { get, post } = useApi();

    const { planType = 'trainer' } = useParams();

    const { setIsLoginModal, ...AuthDetails } = useContext(AuthContext);

    const [isLoading, setIsLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [isSelectingPlan, setIsSelectingPlan] = useState(false);

    // 👇 Guard to prevent multiple API calls in React StrictMode
    const hasFetchedRef = useRef(false);

    const _getPlans = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);

        try {
            const response = await get('plans', {
                params: {
                    category: planType || 'trainer',
                },
            });

            if (response?.status) {
                setPlans(response?.data || []);
            } else {
                setPlans([]);
                setHasError(true);
            }
        } catch (error) {
            console.error('Error fetching plans: ', error);
            setPlans([]);
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, [get, planType]);

    const onSelectPlans = async ({ plan_id = null }) => {
        await setIsSelectingPlan(true);

        try {
            const response = await post('create-order', {
                plan_id: plan_id,
                user_id: AuthDetails?.userId
            });

            // console.log("response ->", response);


            if (response?.status) {
                const responseData = response?.data || {};
                const user_name = AuthDetails?.user?.name || AuthDetails?.user?.facility_name || AuthDetails?.user?.academy_name || "Customer";
                const email = AuthDetails?.user?.email || "";
                const phone_no = AuthDetails?.user?.mobile_no || "";
                const options = {
                    key: responseData.key,
                    amount: responseData.amount,
                    currency: responseData.currency,
                    name: "FWPI Membership",
                    description: "FWPI Membership Plan Purchase",
                    order_id: responseData.order_id,

                    handler: function (rsResponse) {
                        // Payment success
                        // console.log("Payment success:", rsResponse);

                        onSubmitRazorPayResponse({
                            plan_id,
                            paymentStatus: "SUCCESS",
                            ...rsResponse,
                        });
                    },

                    prefill: {
                        name: user_name,
                        email: email || "",
                        contact: phone_no || "",
                    },

                    theme: {
                        color: "#171A26",
                    },

                    modal: {
                        ondismiss: function () {
                            // User closed popup without paying
                            console.log("Payment popup closed by user");

                            // onSubmitRazorPayResponse({
                            //     plan_id,
                            //     paymentStatus: "CANCELLED",
                            // });
                        },
                    },
                };

                // Create Razorpay instance
                const rzp = new window.Razorpay(options);

                // PAYMENT FAILED HANDLER
                rzp.on("payment.failed", function (errorResponse) {
                    console.log("Payment Failed:", errorResponse);

                    onSubmitRazorPayResponse({
                        plan_id,
                        paymentStatus: "FAILED",
                        reason: errorResponse?.error?.description || "Something went wrong",
                        ...errorResponse,
                    });
                });

                // Open Razorpay modal
                rzp.open();
            } else {
                console.error('Error fetching plans: ', response || "Unknown error");
            }
        } catch (error) {
            console.error('Error fetching plans: ', error);
        } finally {
        }
    }

    const onSubmitRazorPayResponse = async ({ plan_id = null, paymentStatus = 'success', ...razorpayRes }) => {

        try {
            const response = await post('payment/verify', {
                plan_id: plan_id,
                user_id: AuthDetails?.userId,
                status: paymentStatus,
                ...razorpayRes
            });

            // console.log("response ->", response)

            if (response?.status) {
                navigate('/', { replace: true });
            } else {
                console.error('Error fetching plans: ', response || "Unknown error");
            }
        } catch (error) {
            console.error('Error fetching plans: ', error);
            // setPlans([]);
        } finally {
            setIsSelectingPlan(false);
        }
    }

    useEffect(() => {
        // ✅ runs only once per mount, even if StrictMode re-runs effects
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        _getPlans();
    }, [_getPlans]);

    return (
        <div className="w-full flex flex-col">
            {/* Header Section */}
            <section className="w-full bg-gradient-radial lg:pt-20 pt-20 lg:pb-5 pb-5 lg:py-28">
                <div className="w-full lg:w-full flex flex-col items-center py-12">
                    <img
                        className="md:w-[22rem] w-[15rem] h-auto"
                        src="/assets/FWPI-logo.png"
                        alt="FWPI Logo"
                    />
                </div>

                <div className="w-full flex flex-col justify-between items-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold font-urbanist text-white text-center">
                        {planType === 'trainer' ? 'FWPI Individual Membership' : 'FWPI Gym Membership'}
                    </h2>
                    <p className="mt-4 text-md text-center text-white py-2 px-4 lg:px-48 font-urbanist">
                        {planType === 'trainer' ? 'Empowering Your Fitness Journey, One Step at a Time' : 'Your Gateway to Professional Fitness Excellence'}
                    </p>
                </div>
            </section>

            <div className="w-full bg-gradient-radial">
                <div className="container mx-auto px-4 sm:px-2 lg:px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto font-urbanist">
                        {isLoading ? (
                            <Fragment>
                                <PricingCardSkeleton />
                                <PricingCardSkeleton />
                            </Fragment>
                        ) : plans.length > 0 ? (
                            plans.map((plan, i) => {
                                // const features = (JSON.parse(plan.features || "[]")).map((ftr) => ({}))
                                return (
                                    <PricingCard
                                        key={i}
                                        loading={isSelectingPlan}
                                        disabled={isSelectingPlan}
                                        {...plan}
                                        onClick={() => {
                                            if (!!AuthDetails?.token) {
                                                onSelectPlans({ plan_id: plan?.id })
                                            } else {
                                                setIsLoginModal(true);
                                                navigate(`/${planType}/create`, { replace: false });// TODO: Redirect to login with plan selection intent
                                            }
                                        }}
                                    />
                                )
                            })
                        ) : (
                            <div className="col-span-1 md:col-span-2 text-center py-10">
                                <p className="text-xl font-semibold text-white">
                                    Membership plans not available
                                </p>
                                <p className="text-sm mt-2 text-gray-300">
                                    {hasError
                                        ? 'We’re having trouble fetching plans right now. Please try again later.'
                                        : 'Currently there are no membership plans available.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export { MembershipPlans };
